import { ReactNode } from "react";
import styles from "../components/InlineMarkdown/InlineMarkdown.module.scss";
import { INLINE_MARKDOWN_REGEX } from "../utils/inlineMarkdownRegex";

/**
 * Parses a limited set of inline markdown constructs in a string and returns React nodes.
 * `lastIndex` is reset at the start of each call and saved/restored around recursive calls
 * so a single shared regex instance is safe for nested parsing.
 *
 * Supported constructs (matched in order):
 * - `***text***` → `<strong><em>text</em></strong>`
 * - `**text**` → `<strong>text</strong>`
 * - `*text*` → `<em>text</em>`
 * - `==text==` → `<mark>`
 * - `` `text` `` → `<code>` (no nested markdown inside code)
 *
 * For plain-text stripping (e.g. aria-label), use `stripInlineMarkdown` from
 * `./stripInlineMarkdown` — it has no style dependencies.
 *
 * @param text - The string to parse
 * @returns Array of React nodes representing the parsed content
 */
export const parseInlineMarkdown = (text: string): ReactNode[] => {
  const parts: ReactNode[] = [];
  let lastIndex = 0;

  INLINE_MARKDOWN_REGEX.lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = INLINE_MARKDOWN_REGEX.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.substring(lastIndex, match.index));
    }

    const key = match.index;
    const savedIndex = INLINE_MARKDOWN_REGEX.lastIndex;

    if (match[1]) {
      // ***bold italic***
      parts.push(
        <strong key={key}>
          <em>{parseInlineMarkdown(match[1])}</em>
        </strong>,
      );
    } else if (match[2]) {
      // **bold**
      parts.push(<strong key={key}>{parseInlineMarkdown(match[2])}</strong>);
    } else if (match[3]) {
      // *italic*
      parts.push(<em key={key}>{parseInlineMarkdown(match[3])}</em>);
    } else if (match[4]) {
      // ==highlight==
      parts.push(
        <mark key={key} className={styles.highlight}>
          {parseInlineMarkdown(match[4])}
        </mark>,
      );
    } else if (match[5]) {
      // `code` — no recursion inside code
      parts.push(
        <code key={key} className={styles.code}>
          {match[5]}
        </code>,
      );
    }

    INLINE_MARKDOWN_REGEX.lastIndex = savedIndex;
    lastIndex = savedIndex;
  }

  if (lastIndex < text.length) {
    parts.push(text.substring(lastIndex));
  }

  return parts;
};
